import { ChoiceField, Field, FieldGrid, FieldSet, Toggle } from "../../components/Fields";
import { I } from "../../components/icons";
import { NavRow, RowGroup } from "../../components/Rows";
import { Segmented } from "../../components/Segmented";
import { KCAL_PER_KG_FAT } from "../../domain/deficit";
import { OLDER_MAX_DEFICIT_KCAL, ageBand, paceWord, proteinSetting } from "../../domain/eating";
import { deficitTarget } from "../../domain/intake";
import type { KayaState, Profile as ProfileT } from "../../domain/types";
import { useDeviceAnswers, useKaya } from "../../store/kayaStore";
import { useProfileForm } from "./profileForm";
import SettingsPage from "./SettingsPage";

/** Who is training and what they are training towards — the numbers every other figure derives
 *  from. Height, weight and year of birth are what unlock the body figures on Progress. */
export default function BodyGoals() {
  const { f, set, save, dirty } = useProfileForm();
  const S = useKaya((s) => s.S);
  const device = useDeviceAnswers();
  // The band follows the year as typed, so the line under the box answers before Save.
  const by = Number(f.birthYear);
  const asTyped: KayaState = {
    ...S,
    profile: { ...S.profile, birthYear: Number.isInteger(by) && by >= 1900 ? by : undefined },
  };
  const band = ageBand(asTyped);
  const pace = deficitTarget(S, undefined, device);

  return (
    <SettingsPage
      title="Body & goals"
      intro="Resting burn, BMI, body fat and stride all come from these."
      onSave={() => void save()}
      dirty={dirty}
    >
      <FieldSet legend="About you">
        <Field label="Name" type="text" autoComplete="name" value={f.name} onChange={(v) => set("name", v)} />
        <FieldGrid>
          <Field
            label="Height"
            unit="cm"
            type="number"
            step="1"
            value={f.heightCm}
            onChange={(v) => set("heightCm", v)}
          />
          <Field
            label="Starting weight"
            unit="kg"
            type="number"
            step="0.1"
            value={f.startWeight}
            onChange={(v) => set("startWeight", v)}
          />
        </FieldGrid>
        {/* A year rather than an age, so it does not silently go stale. Resting burn cannot be
            worked out without it. */}
        <Field
          label="Year of birth"
          type="number"
          step="1"
          value={f.birthYear}
          onChange={(v) => set("birthYear", v)}
          hint={
            band === "60+" ? (
              <>
                <strong>Age band 60+</strong> · protein {proteinSetting(asTyped)} g/kg, deficit capped at{" "}
                {OLDER_MAX_DEFICIT_KCAL}
                {f.sex === "female" ? ", iron the adult figure" : ""}
              </>
            ) : (
              "Unlocks resting burn"
            )
          }
        />
        <ChoiceField label="Sex">
          <Segmented
            ariaLabel="Sex"
            value={f.sex}
            options={[
              { value: "male", label: "Male" },
              { value: "female", label: "Female" },
              { value: "", label: "Not set" },
            ]}
            onChange={(v) => {
              set("sex", v);
              // picking female turns tracking on, as in the original — unless periods have stopped
              if (v === "female" && !f.periodsStopped) set("cycleOn", true);
            }}
          />
          <p className="k-field__hint">Changes the burn and body-fat formulas</p>
        </ChoiceField>
        {f.sex === "female" ? (
          <ChoiceField label="Cycle">
            {/* A state, not a deletion: the logged periods stay on file (ADR-039). */}
            <Toggle
              title="Periods have stopped"
              sub={
                f.periodsStopped
                  ? "Period predictions and the 29 mg iron figure are retired"
                  : "Switch on when periods have stopped; what you logged is kept"
              }
              on={f.periodsStopped}
              onChange={(v) => {
                set("periodsStopped", v);
                set("cycleOn", !v);
              }}
            />
          </ChoiceField>
        ) : null}
      </FieldSet>

      <FieldSet legend="Goal">
        <Segmented<ProfileT["goal"]>
          ariaLabel="Goal"
          value={f.goal}
          options={[
            { value: "weight", label: <>{I.scale}Weight</> },
            { value: "waist", label: <>{I.tape}Waist</> },
          ]}
          onChange={(v) => set("goal", v)}
        />
        {f.goal === "weight" ? (
          <Field
            label="Target weight"
            unit="kg"
            type="number"
            step="0.1"
            value={f.targetWeight}
            onChange={(v) => set("targetWeight", v)}
          />
        ) : (
          <Field
            label="Target waist"
            unit="cm"
            type="number"
            step="0.5"
            value={f.targetWaist}
            onChange={(v) => set("targetWaist", v)}
          />
        )}
        {/* After 60 the pace is capped, so this page says so and links to where it is set. */}
        {band === "60+" ? (
          <ChoiceField label="Pace">
            <RowGroup>
              <NavRow
                title={paceWord(pace)}
                subtitle={`Under Out each day · capped at ${OLDER_MAX_DEFICIT_KCAL} after 60`}
                value={`${pace} kcal`}
                to="/you/food"
              />
            </RowGroup>
            <p className="k-field__hint">
              About {((pace * 7) / KCAL_PER_KG_FAT).toFixed(2)} kg a week — a direction, not a promise.
              Set under Food &amp; calories.
            </p>
          </ChoiceField>
        ) : null}
      </FieldSet>

      <FieldSet legend="Daily goals" hint="Work only — sessions, treadmill, walks.">
        <FieldGrid cols={3}>
          <Field
            label="Steps"
            type="number"
            step="500"
            value={f.stepGoal}
            onChange={(v) => set("stepGoal", v)}
          />
          <Field
            label="Work"
            unit="kcal"
            type="number"
            step="25"
            value={f.kcalGoal}
            onChange={(v) => set("kcalGoal", v)}
          />
          <Field
            label="Moving"
            unit="min"
            type="number"
            step="5"
            value={f.moveMinGoal}
            onChange={(v) => set("moveMinGoal", v)}
          />
        </FieldGrid>
      </FieldSet>
    </SettingsPage>
  );
}
